import { Observable } from 'rxjs';
import { getNativeFetch } from './native-fetch.js';
export const cancelSymbol = Symbol('abort');
export const abortErrorName = 'AbortError';
const nativeFetch = getNativeFetch();
/**
 * Cancels cancellable fetch
 *
 * @param request - CancellableFetchPromise - Promise that resolves to the Response and could be cancelled.
 *
 * @public
 */
export const cancelFetch = (request) => {
    var _a;
    (_a = request[cancelSymbol]) === null || _a === void 0 ? void 0 : _a.call(request);
};
/**
 * Starts the process of fetching a resource from the network and returns Promise.
 * The first 2 parameters are identical to those of the Request() constructor {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request}.
 *
 * @param input - Defines the resource that you wish to fetch. This can either be: a string or a Request object.
 * @param init - An object containing any custom settings that you want to apply to the request.
 * @param rejectOnCancel - If `true` Rejects promise on request cancellation. `false` by default.
 * @returns CancellableFetchPromise that resolves to the Response and could be cancelled using cancelFetch method.
 *
 * @public
 */
export function fetch(input, init = {}, rejectOnCancel = false) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fetchErrorHandler = (error) => {
        const doNotRejectOnError = error instanceof DOMException && error.name === abortErrorName && !rejectOnCancel;
        return doNotRejectOnError ? error : Promise.reject(error);
    };
    const controller = new AbortController();
    const signal = controller.signal;
    const response = nativeFetch(input, { ...init, signal }).catch(fetchErrorHandler);
    response[cancelSymbol] = () => controller.abort();
    return response;
}
/**
 * Starts the process of fetching a resource from the network and returns an Observable.
 * The parameters are identical to those of the Request() constructor {@link https://developer.mozilla.org/en-US/docs/Web/API/Request/Request}.
 *
 * @remarks
 *
 * The request will get canceled when there are no active subscriptions.
 *
 * @param input - This defines the resource that you wish to fetch. This can either be: a string or a Request object.
 * @param init - An object containing any custom settings that you want to apply to the request.
 * @returns Observable that resolves to the Response.
 *
 * @public
 */
export function fetch$(input, init) {
    let response;
    let observersCount = 0;
    return new Observable((observer) => {
        if (observersCount === 0 && response === undefined) {
            response = fetch(input, init, true);
        }
        ++observersCount;
        response === null || response === void 0 ? void 0 : response.then(res => {
            observer.next(res);
            response = undefined;
            observer.complete();
        }).catch(error => {
            if (error instanceof DOMException && error.name === abortErrorName) {
                observer.complete();
            }
            else {
                response = undefined;
                observer.error(error);
            }
        });
        return () => {
            --observersCount;
            if (observersCount === 0 && response !== undefined) {
                cancelFetch(response);
                response = undefined;
            }
        };
    });
}
